// ─── Onboarding guides ────────────────────────────────────────────────────────
// The short, task-shaped walkthroughs a new account works through to get from
// an empty workspace to a live embedded integration. Rendered by GuidesPage and
// summarised by GettingStartedCard on the home page.

import type { SystemRole } from "@/lib/rbac";

export type GuideId =
  | "connect-first-app"
  | "build-first-workflow"
  | "embed-widget"
  | "connect-mcp";

export interface GuideStep {
  title: string;
  body: string;
  /** In-app route this step is performed on. */
  to?: string;
}

/**
 * Lowest role that can act on the Connect-to-Claude flow.
 *
 * Shared by the guide below and by every entry point that opens the dialog
 * (both home pages, the Setup Assistant rail) so the gates cannot drift — a
 * button that opens onto a guide the role cannot reach is worse than no button.
 */
export const CONNECT_MCP_MIN_ROLE: SystemRole = "developer";

export interface Guide {
  id: GuideId;
  title: string;
  summary: string;
  /** Rough time to complete, in minutes. */
  minutes: number;
  /** Lowest role that can actually finish the guide. */
  minRole: SystemRole;
  actionLabel: string;
  /**
   * Route the primary action opens. Omitted when the action is a dialog rather
   * than a navigation — `connect-mcp` opens the Connect-to-MCP dialog.
   */
  to?: string;
  /**
   * Whether completion can be observed from account state.
   *
   * False for anything that happens outside this product, which can only ever
   * be ticked by hand. The home card keys "this account is set up" off the
   * auto-detectable guides only — otherwise a fully onboarded org is nagged
   * forever by a step we are structurally unable to see finished.
   */
  autoDetectable: boolean;
  steps: GuideStep[];
}

export const GUIDES: Guide[] = [
  {
    id: "connect-first-app",
    title: "Connect your first app",
    summary:
      "Authorize a third-party app so fastn can read and write data on your behalf.",
    minutes: 2,
    // developer, not operator: completion is detected from the saasPool
    // connections list, which by design never surfaces the tenant-scoped rows
    // an operator creates — the guide could only ever sit at 0 of 1 for them.
    minRole: "developer",
    actionLabel: "Browse connectors",
    autoDetectable: true,
    to: "/integrations",
    steps: [
      {
        title: "Pick a connector",
        body: "Open Integrations and choose the app you want to connect. If the app you need is not in the catalog, the AI connector builder can generate one from its API docs.",
        to: "/integrations",
      },
      {
        title: "Authorize it",
        body: "Click Connect. OAuth apps open a popup to sign in; API-key and Basic-auth apps ask for the credential inline. Credentials are encrypted with AES-256-GCM and never returned by the API.",
      },
      {
        title: "Run a test action",
        body: "Open the connector's Actions tab and execute a read action. A green response confirms the credential works and the connector is scoped to your account.",
      },
    ],
  },
  {
    id: "build-first-workflow",
    title: "Build your first workflow",
    summary:
      "Write the logic that moves data between the apps you connected, and run it.",
    minutes: 5,
    minRole: "developer",
    actionLabel: "Open workflows",
    autoDetectable: true,
    to: "/integrations?tab=workflows",
    steps: [
      {
        title: "Create a workflow",
        body: "From Integrations, open the Workflows tab and create one. Workflows are plain JavaScript executed in an isolated sandbox — no DSL to learn.",
        to: "/integrations?tab=workflows",
      },
      {
        title: "Call your connectors",
        body: "Use fastn.connector(...) to invoke any action on a connected app. Field mapping and type coercion are handled for you, so you work with the canonical data model rather than each vendor's payload shape.",
      },
      {
        title: "Pick an execution tier",
        body: "Instant runs inline and returns a response. Standard and long run on Temporal with retries and durable state — use those for syncs and anything that can take more than a few seconds.",
      },
      {
        title: "Run it and read the trace",
        body: "Execute the workflow, then open Activity to see the run, its inputs and outputs, and every connector call it made.",
        to: "/activity",
      },
    ],
  },
  {
    id: "embed-widget",
    title: "Embed the widget in your product",
    summary:
      "Ship the integrations you built to your own customers with one script tag.",
    minutes: 5,
    minRole: "developer",
    actionLabel: "Open widgets",
    autoDetectable: true,
    to: "/widgets",
    steps: [
      {
        title: "Create a widget",
        body: "A widget is the catalog your customers see. Choose which connectors and workflows it exposes — everything else stays private to your account.",
        to: "/widgets",
      },
      {
        title: "Preview it as a tenant",
        body: "Use the widget preview to see exactly what an end user sees, including the connect flow and any configuration fields you require.",
      },
      {
        title: "Drop it into your app",
        body: "Copy a snippet from the widget's Embed tab: an iframe tag for anywhere you only control HTML, or the SDK (npm install @fastn-ai/embed) if you can run JavaScript and want connect events and token refresh. Either way the hub renders in an iframe served by fastn, so it cannot collide with your own styles.",
      },
      {
        title: "Watch the first activation",
        body: "When one of your customers connects an app through the widget, it appears under the widget's installations with its own scoped credentials.",
      },
    ],
  },
  {
    id: "connect-mcp",
    title: "Connect fastn to your favourite agent",
    summary:
      "Add fastn as an MCP server so Claude, Cursor, Codex and the rest can build and run integrations in fastn for you.",
    minutes: 1,
    minRole: CONNECT_MCP_MIN_ROLE,
    actionLabel: "Connect to your favourite agent",
    autoDetectable: false,
    steps: [
      {
        title: "Open the install link",
        body: "Claude, Cursor and VS Code each take a one-click link that opens the tool with fastn's MCP server already filled in. None of them will add a server from a link outright, so you still review the values and authorize fastn. That step is the security boundary, not a missing feature.",
      },
      {
        title: "Or paste the URL yourself",
        body: "Every client accepts the same fastn MCP server URL. The dialog has a copy button for it, and ChatGPT, Lovable and Bolt are set up by pasting it into their own connector page.",
      },
      {
        title: "Command line and config files",
        body: "Claude Code, Codex and Gemini CLI each take a one-line command. Antigravity, Windsurf, Cline and Claude Desktop take a config file entry, and the dialog carries the exact snippet and the path it goes in, because the shapes are not interchangeable between them.",
      },
      {
        title: "Your whole organisation",
        body: "On a Claude Team or Enterprise plan, an owner can add fastn once and everyone in the workspace gets it. Nobody else has to repeat any of these steps.",
      },
      {
        title: "Ask it to use fastn",
        body: "Once connected, your agent has fastn's tools for building integrations. Ask it to wire up a connector or put a workflow together and it does the work in fastn.",
      },
    ],
  },
];

export function getGuide(id: string | undefined): Guide | undefined {
  return GUIDES.find((g) => g.id === id);
}
